<template>
  <div class="route-create-wrapper" :style="`height: ${isFull ? 'calc(100% - 78px)' : '640px'}`">
    <div class="route-create-body">
      <!-- 左侧：地图区域 -->
      <div class="map-panel">
        <!-- <div class="map-header">
          <div class="map-title">
            <i class="el-icon-location-outline"></i>
            {{ isEdit ? $t('smartRouting.mapCenter.pathPlanning.routeCreate.editTitle') : $t('smartRouting.mapCenter.pathPlanning.routeCreate.title') }}
            <span class="route-id" v-if="routeId">· {{ routeId }}</span>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <el-button size="small" @click="zoomIn" icon="el-icon-zoom-in">{{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.zoomIn') }}</el-button>
            <el-button size="small" @click="zoomOut" icon="el-icon-zoom-out">{{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.zoomOut') }}</el-button>
            <el-button size="small" @click="resetView" icon="el-icon-refresh">
              {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.resetView') }}
            </el-button>
          </div>
        </div> -->

        <!-- <div class="map-hint">
          <i class="el-icon-info"></i>
          {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.mapHint') }}{{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.mapHintDetail') }}
        </div> -->

        <!-- 地图容器 -->
        <div
          class="map-container"
          ref="mapContainer"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
          @wheel.prevent="handleWheel"
        >
          <svg
            v-if="mapReady"
            width="100%"
            height="100%"
            :viewBox="`0 0 ${mapNaturalWidth} ${mapNaturalHeight}`"
            preserveAspectRatio="none"
            class="map-svg"
            style="display:block;"
          >
            <g :transform="`translate(${panX}, ${panY}) scale(${zoom})`">
              <image
                :href="currentMapUrl"
                x="0" y="0"
                :width="mapNaturalWidth"
                :height="mapNaturalHeight"
                preserveAspectRatio="none"
                draggable="false"
                style="pointer-events: none;"
              />

              <polyline
                v-if="waypoints.length > 1"
                :points="waypoints.map(p => p.x + ',' + p.y).join(' ')"
                fill="none"
                stroke="#409eff"
                stroke-width="2"
                stroke-dasharray="6,4"
                style="pointer-events: none;"
              />

              <circle
                v-for="(point, index) in waypoints"
                :key="'halo-' + index"
                :cx="point.x"
                :cy="point.y"
                :r="5 / zoom"
                :fill="point.id === editingId ? '#faad14' : '#67c23a'"
                fill-opacity="0.15"
                stroke="none"
                style="pointer-events: none;"
              />

              <circle
                v-for="(point, index) in waypoints"
                :key="'dot-' + index"
                :cx="point.x"
                :cy="point.y"
                :r="2.5 / zoom"
                :fill="point.id === editingId ? '#faad14' : '#67c23a'"
                @click.stop="selectWaypoint(index)"
                style="cursor:pointer; pointer-events: auto;"
              />

              <text
                v-for="(point, index) in waypoints"
                :key="'label-' + index"
                :x="point.x + 6 / zoom"
                :y="point.y - 6 / zoom"
                :font-size="9 / zoom"
                fill="#1d2129"
                font-weight="500"
                style="pointer-events: none;"
              >
                #{{ index + 1 }}
              </text>

              <circle
                v-if="mousePosition && !isDragging"
                :cx="mousePosition.x"
                :cy="mousePosition.y"
                :r="3 / zoom"
                fill="#409eff"
                fill-opacity="0.4"
                stroke="#409eff"
                :stroke-width="1 / zoom"
                style="pointer-events: none;"
              />
              <text
                v-if="mousePosition && !isDragging"
                :x="mousePosition.x + 6 / zoom"
                :y="mousePosition.y - 6 / zoom"
                :font-size="9 / zoom"
                fill="#409eff"
                font-weight="500"
                style="pointer-events: none;"
              >
                ({{ fmtWorld(mousePosition.worldX) }}m, {{ fmtWorld(mousePosition.worldY) }}m)
              </text>
            </g>
          </svg>

          <div v-else class="map-loading">{{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.mapLoading') }}</div>

          <div class="map-zoom-info" v-if="mapReady">
            {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.zoomInfo', { percent: (zoom * 100).toFixed(0) }) }}
          </div>

          <div class="map-coords" v-if="hoverCoords && mapReady">
            <span>X: {{ fmtWorld(hoverCoords.worldX) }}m</span>
            <span>Y: {{ fmtWorld(hoverCoords.worldY) }}m</span>
          </div>
        </div>
      </div>

      <!-- 右侧：表单面板 -->
      <div class="form-panel" :style="`width: ${isFull ? '600px' : '550px'}`">
        <div class="form-scroll">
          <el-form
            ref="formRef"
            :model="formData"
            :rules="formRules"
            label-width="80px"
            label-position="right"
            class="create-form"
          >
            <el-form-item
              :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.routeName')"
              prop="name"
              required
            >
              <el-input
                v-model="formData.name"
                :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.routeNamePlaceholder')"
                maxlength="50"
                size="default"
              />
            </el-form-item>

            <el-form-item
              :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.associateMap')"
              prop="mapCode"
              required
            >
              <el-select
                v-model="formData.mapCode"
                :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.associateMap')"
                style="width:100%"
                size="default"
                @change="handleMapChange"
              >
                <el-option
                  v-for="item in mapList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>

            <el-form-item
              :label="$t('smartRouting.voiceLibrary.title')"
              prop="voiceCode"
            >
              <el-select
                v-model="formData.voiceCode"
                :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.voicePlaceholder')"
                style="width:100%"
                size="default"
              >
                <el-option
                  v-for="item in voiceList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>

            <el-form-item
              :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.routeDesc')"
              prop="desc"
            >
              <el-input
                v-model="formData.desc"
                :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.routeDescPlaceholder')"
                type="textarea"
                :rows="4"
                maxlength="200"
                resize="none"
              />
            </el-form-item>
          </el-form>

          <div class="waypoints-section">
            <div class="waypoints-header">
              <span class="waypoints-title">
                {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.waypoints', { count: waypoints.length }) }}
              </span>
              <el-button type="text" size="small" @click="addWaypoint" icon="el-icon-plus">
                {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.addWaypoint') }}
              </el-button>
            </div>

            <el-table
              ref="waypointsTableRef"
              :data="waypoints"
              size="small"
              class="waypoints-table"
              row-key="id"
              @expand-change="handleExpandChange"
              @row-click="handleRowClick"
              :expand-row-keys="expandedKeys"
              :max-height="isFull ? 440 : 320"
            >
              <el-table-column type="expand" width="1">
                <template #default="{ row, $index }">
                  <div class="waypoint-edit" @click.stop>
                    <div class="edit-header">
                      <span class="edit-title">
                        {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointEdit', { id: $index + 1 }) }}
                      </span>
                      <el-button
                        type="text"
                        size="mini"
                        style="color:#f56c6c"
                        @click="removeWaypoint($index)"
                        icon="el-icon-delete"
                      >
                        {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.deleteWaypoint') }}
                      </el-button>
                    </div>

                    <el-form :model="row" label-width="120px" size="small">
                      <el-form-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointName')">
                        <el-input v-model="row.name" :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointNamePlaceholder')" />
                      </el-form-item>

                      <el-form-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.yaw')">
                        <el-input-number style="width:100%" v-model="row.yaw" controls-position="right"></el-input-number>
                      </el-form-item>

                      <el-form-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.actionLibrary')">
                        <el-select
                          v-model="row.actionCode"
                          :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.actionPlaceholder')"
                          style="width:100%"
                        >
                          <el-option
                            v-for="item in actionList"
                            :key="item.code"
                            :label="item.name"
                            :value="item.code"
                          />
                        </el-select>
                      </el-form-item>

                      <el-form-item :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.speechText')">
                        <el-input
                          v-model="row.speechText"
                          type="textarea"
                          :rows="2"
                          :placeholder="$t('smartRouting.mapCenter.pathPlanning.routeCreate.speechTextPlaceholder')"
                          resize="none"
                        />
                      </el-form-item>
                    </el-form>
                  </div>
                </template>
              </el-table-column>

              <el-table-column width="20" align="center">
                <template #default>
                  <i class="el-icon-rank drag-handle" style="cursor:grab;color:#c0c4cc;"></i>
                </template>
              </el-table-column>

              <el-table-column prop="name" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointName')" min-width="70">
                <template #default="{ row, $index }">
                  <span class="waypoint-index">#{{ $index + 1 }}</span>
                  <span class="waypoint-name">{{ row.name || '-' }}</span>
                </template>
              </el-table-column>

              <el-table-column prop="x" label="X" width="55">
                <template #default="{ row }">{{ row.x ?? 0 }}</template>
              </el-table-column>

              <el-table-column prop="y" label="Y" width="55">
                <template #default="{ row }">{{ row.y ?? 0 }}</template>
              </el-table-column>

              <el-table-column prop="worldX" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.worldCoordX')" width="70">
                <template #default="{ row }">{{ fmtWorld(row.worldX) }}m</template>
              </el-table-column>

              <el-table-column prop="worldY" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.worldCoordY')" width="70">
                <template #default="{ row }">{{ fmtWorld(row.worldY) }}m</template>
              </el-table-column>

              <el-table-column prop="yaw" label="yaw" width="40">
                <template #default="{ row }">{{ row.yaw || 0 }}°</template>
              </el-table-column>

              <el-table-column prop="speechText" :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointResource')" min-width="80">
                <template #default="{ row }">
                  <el-popover
                    placement="top-start"
                    :title="$t('smartRouting.mapCenter.pathPlanning.routeCreate.speechText')"
                    :width="200"
                    trigger="hover"
                  >
                    <template #default>
                      <div style="max-height: 300px;overflow: auto;">{{row.speechText}}</div>
                    </template>
                    <template #reference>
                      <span class="ellipsis-text">{{ row.speechText }}</span>
                    </template>
                  </el-popover>
                  <el-tag v-if="row.actionName" size="small">{{ row.actionName }}</el-tag>
                </template>
              </el-table-column>

              <el-table-column :label="$t('smartRouting.mapCenter.pathPlanning.routeCreate.delete')" width="50" align="center">
                <template #default="{ $index }">
                  <el-button
                    type="text"
                    size="mini"
                    style="color:#f56c6c"
                    @click.stop="removeWaypoint($index)"
                    icon="el-icon-delete"
                  />
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="route-create-footer">
    <div class="footer-info">
      {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.coordSystemInfo', { res: mapConfig.res, ox: mapConfig.ox, oy: mapConfig.oy, yaw: (mapConfig.yaw0 * 180 / Math.PI).toFixed(1) }) }}
      <br>
      <!-- 比例尺 1m ≈ {{ metersToPixels(1) }} 像素 （1像素 = {{ mapConfig.res }}m）
      <br> -->
      <!-- <span style="color:#c0c4cc">
        ※ 严格按 Nav2 公式：u_grid=(u+0.5)·res，v_grid=(H-1-v)·res，世界坐标保留 3 位小数
      </span> -->
    </div>
    <div class="footer-actions">
      <el-button @click="handleCancel">
        {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.cancel') }}
      </el-button>
      <el-button type="primary" @click="handleSave" :loading="saving">
        {{ $t('smartRouting.mapCenter.pathPlanning.routeCreate.save') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { reqPathCreate, reqPathUpdate, reqMapList, reqVoiceList, reqActionList } from '@/api/map'
import Sortable from 'sortablejs'

export default {
  name: 'RouteCreate',
  props: {
    routeId: { type: String, default: '' },
    routeCode: { type: String, default: '' },
    isEdit: { type: Boolean, default: false },
    editData: { type: Object, default: null },
    isFullscreen: { type: Boolean, default: false }
  },
  emits: ['save', 'cancel', 'success'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const formRef = ref(null)
    const saving = ref(false)
    const hoverCoords = ref(null)
    const expandedKeys = ref([])
    const mapContainer = ref(null)
    const mousePosition = ref(null)
    const editingId = ref(null)
    const waypointsTableRef = ref(null)

    const zoom = ref(1)
    const panX = ref(0)
    const panY = ref(0)

    const isDragging = ref(false)
    const hasMoved = ref(false)
    const dragStartX = ref(0)
    const dragStartY = ref(0)
    const panStartX = ref(0)
    const panStartY = ref(0)

    const mapList = ref([])
    const voiceList = ref([])
    const actionList = ref([])

    const currentMapUrl = ref('/nav2_static_cost_preview.png')

    // ===== 地图配置：与 Nav2 PGM+YAML 对应 =====
    const mapConfig = reactive({
      res: 0.05,
      ox: -14.3,
      oy: -5.68,
      yaw0: 0
    })

    const mapNaturalWidth = ref(0)
    const mapNaturalHeight = ref(0)
    const isFull = ref(false)

    // ✅ 地图就绪标志
    const mapReady = computed(() =>
      mapNaturalWidth.value > 0 && mapNaturalHeight.value > 0
    )

    const editDataInitialized = ref(false)
    const needRecalcPixel = ref(false)

    const formRules = {
      name: [
        { required: true, message: t('smartRouting.mapCenter.pathPlanning.routeCreate.routeName'), trigger: 'blur' }
      ],
      mapCode: [
        { required: true, message: t('smartRouting.mapCenter.pathPlanning.routeCreate.associateMap'), trigger: 'change' }
      ]
    }

    const waypoints = ref([])
    let nextId = 10

    const formData = reactive({
      name: '',
      mapCode: '',
      pathType: '',
      voiceCode: '',
      deviceChannel: '',
      desc: ''
    })

    const pathTypeOptions = [
      { label: t('smartRouting.mapCenter.pathPlanning.routeManagement.pathTypeGuide'), value: 'guide' },
      { label: t('smartRouting.mapCenter.pathPlanning.routeManagement.pathTypeOperation'), value: 'operation' },
      { label: t('smartRouting.mapCenter.pathPlanning.routeManagement.pathTypeInspection'), value: 'inspection' }
    ]

    // ===== 比例尺 =====
    const metersToPixels = (meters) => {
      if (!mapConfig.res) return '—'
      const px = meters / mapConfig.res
      return px >= 100 ? px.toFixed(0) : px.toFixed(1)
    }

    // ===== 世界坐标显示：保留 3 位小数 =====
    const fmtWorld = (v) => {
      if (v == null || !isFinite(v)) return '0.000'
      return Number(v).toFixed(3)
    }

    // ===== 坐标转换：严格按 Nav2 官方公式 =====
    //
    //  u_grid = (u + 0.5) * res0
    //  v_grid = (H - 1 - v) * res0
    //  x_map  = x0 + cos(yaw0)*u_grid - sin(yaw0)*v_grid
    //  y_map  = y0 + sin(yaw0)*u_grid + cos(yaw0)*v_grid
    //
    //  反解：
    //  dx = x_map - x0
    //  dy = y_map - y0
    //  u_grid =  cos(yaw0)*dx + sin(yaw0)*dy
    //  v_grid = -sin(yaw0)*dx + cos(yaw0)*dy
    //  u = u_grid / res0 - 0.5
    //  v = H - 1 - v_grid / res0
    //

    // ✅ viewBox 坐标 (vx, vy) -> PGM 像素 (u, v)，clamp 到 [0, W-1] × [0, H-1]
    const viewBoxToPixel = (vx, vy) => {
      if (!mapReady.value) return { pixelX: 0, pixelY: 0 }
      const W = mapNaturalWidth.value
      const H = mapNaturalHeight.value
      return {
        pixelX: Math.min(W - 1, Math.max(0, Math.round(vx))),
        pixelY: Math.min(H - 1, Math.max(0, Math.round(vy)))
      }
    }

    // 图像像素 (u, v) -> 世界坐标 (x_map, y_map)
    const pixelToWorld = (u, v) => {
      if (!mapReady.value) return { worldX: 0, worldY: 0 }
      const H = mapNaturalHeight.value
      const res0 = mapConfig.res
      const x0 = mapConfig.ox
      const y0 = mapConfig.oy
      const yaw0 = mapConfig.yaw0 || 0

      const uGrid = (u + 0.5) * res0
      const vGrid = (H - 1 - v) * res0

      const cosYaw = Math.cos(yaw0)
      const sinYaw = Math.sin(yaw0)

      const wX = x0 + cosYaw * uGrid - sinYaw * vGrid
      const wY = y0 + sinYaw * uGrid + cosYaw * vGrid

      return {
        worldX: Math.round(wX * 1000) / 1000,
        worldY: Math.round(wY * 1000) / 1000
      }
    }

    // 世界坐标 (x_map, y_map) -> 图像像素 (u, v)
    const worldToPixel = (wx, wy) => {
      if (!mapReady.value) return { pixelX: 0, pixelY: 0 }
      const H = mapNaturalHeight.value
      const res0 = mapConfig.res
      const x0 = mapConfig.ox
      const y0 = mapConfig.oy
      const yaw0 = mapConfig.yaw0 || 0

      const dx = wx - x0
      const dy = wy - y0

      const cosYaw = Math.cos(yaw0)
      const sinYaw = Math.sin(yaw0)

      const uGrid =  cosYaw * dx + sinYaw * dy
      const vGrid = -sinYaw * dx + cosYaw * dy

      const u = uGrid / res0 - 0.5
      const v = H - 1 - vGrid / res0

      return {
        pixelX: Math.round(u),
        pixelY: Math.round(v)
      }
    }

    // 屏幕坐标 -> viewBox / pixel / world，未就绪时返回 null
    const screenToViewBox = (clientX, clientY) => {
      if (!mapReady.value) return null
      const svg = document.querySelector('.map-svg')
      if (!svg) return null

      const rect = svg.getBoundingClientRect()
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value

      const percentX = (clientX - rect.left) / rect.width
      const percentY = (clientY - rect.top) / rect.height

      const viewBoxX = ((percentX * vbWidth) - panX.value) / zoom.value
      const viewBoxY = ((percentY * vbHeight) - panY.value) / zoom.value

      const { pixelX, pixelY } = viewBoxToPixel(viewBoxX, viewBoxY)
      const { worldX, worldY } = pixelToWorld(pixelX, pixelY)

      return { x: viewBoxX, y: viewBoxY, pixelX, pixelY, worldX, worldY }
    }

    // 开发期自检
    const assertRoundTrip = (pixelX, pixelY) => {
      if (process.env.NODE_ENV === 'production') return
      const { worldX, worldY } = pixelToWorld(pixelX, pixelY)
      const back = worldToPixel(worldX, worldY)
      if (back.pixelX !== pixelX || back.pixelY !== pixelY) {
        console.warn('[坐标往返不一致]', { pixelX, pixelY, worldX, worldY, back })
      }
    }

    // ===== 鼠标事件 =====
    const handleMouseDown = (e) => {
      if (e.target && e.target.tagName && e.target.tagName.toLowerCase() === 'circle') return
      if (e.target.closest && e.target.closest('.el-button')) return

      e.preventDefault()
      isDragging.value = true
      hasMoved.value = false
      dragStartX.value = e.clientX
      dragStartY.value = e.clientY
      panStartX.value = panX.value
      panStartY.value = panY.value
    }

    const handleMouseMove = (e) => {
      const pos = screenToViewBox(e.clientX, e.clientY)
      if (!pos) {
        mousePosition.value = null
        hoverCoords.value = null
        return
      }
      mousePosition.value = pos
      hoverCoords.value = { worldX: pos.worldX, worldY: pos.worldY }

      if (!isDragging.value) return

      const dx = e.clientX - dragStartX.value
      const dy = e.clientY - dragStartY.value

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasMoved.value = true

      const svg = document.querySelector('.map-svg')
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const scaleX = mapNaturalWidth.value / rect.width
      const scaleY = mapNaturalHeight.value / rect.height

      panX.value = panStartX.value + dx * scaleX * zoom.value
      panY.value = panStartY.value + dy * scaleY * zoom.value
      clampPan()
    }

    const handleMouseUp = (e) => {
      if (isDragging.value && !hasMoved.value) {
        handleMapClick(e)
      }
      isDragging.value = false
      setTimeout(() => { hasMoved.value = false }, 50)
    }

    const handleMouseLeave = () => {
      isDragging.value = false
      mousePosition.value = null
      hoverCoords.value = null
    }

    // ✅ 打点：x/y 直接用整数像素
    const handleMapClick = (event) => {
      const pos = screenToViewBox(event.clientX, event.clientY)
      if (!pos) return
      const { pixelX, pixelY, worldX, worldY } = pos

      const hitRadius = 10 / zoom.value
      const hitIndex = waypoints.value.findIndex(p => {
        const dx = Math.abs(p.x - pixelX)
        const dy = Math.abs(p.y - pixelY)
        return dx < hitRadius && dy < hitRadius
      })

      if (hitIndex !== -1) {
        selectWaypoint(hitIndex)
        return
      }

      const newPoint = {
        id: nextId++,
        name: `途经点${waypoints.value.length + 1}`,
        x: pixelX,
        y: pixelY,
        worldX,
        worldY,
        yaw: 0, actionCode: '', speechText: '', resource: ''
      }

      waypoints.value.push(newPoint)
      editingId.value = newPoint.id
      expandedKeys.value = [newPoint.id]
      assertRoundTrip(pixelX, pixelY)
      ElMessage.success(t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointAdded', { index: waypoints.value.length }))
    }

    const handleWheel = (event) => {
      if (!mapReady.value) return
      const svg = document.querySelector('.map-svg')
      if (!svg) return

      const delta = event.deltaY > 0 ? 0.9 : 1.1
      const newZoom = Math.min(5, Math.max(0.2, zoom.value * delta))
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value

      const rect = svg.getBoundingClientRect()
      const percentX = (event.clientX - rect.left) / rect.width
      const percentY = (event.clientY - rect.top) / rect.height

      const beforeX = (percentX * vbWidth - panX.value) / zoom.value
      const beforeY = (percentY * vbHeight - panY.value) / zoom.value

      zoom.value = newZoom
      panX.value = percentX * vbWidth - beforeX * zoom.value
      panY.value = percentY * vbHeight - beforeY * zoom.value
      clampPan()
    }

    const clampPan = () => {
      if (!mapReady.value) return
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value
      const maxPanX = vbWidth * (zoom.value - 1) / 2
      const maxPanY = vbHeight * (zoom.value - 1) / 2
      panX.value = Math.max(-maxPanX, Math.min(maxPanX, panX.value))
      panY.value = Math.max(-maxPanY, Math.min(maxPanY, panY.value))
    }

    const zoomIn = () => {
      if (!mapReady.value) return
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value
      const centerX = vbWidth / 2
      const centerY = vbHeight / 2
      const oldZoom = zoom.value
      zoom.value = Math.min(5, zoom.value * 1.2)
      panX.value = centerX - (centerX - panX.value) * (zoom.value / oldZoom)
      panY.value = centerY - (centerY - panY.value) * (zoom.value / oldZoom)
      clampPan()
    }

    const zoomOut = () => {
      if (!mapReady.value) return
      const vbWidth = mapNaturalWidth.value
      const vbHeight = mapNaturalHeight.value
      const centerX = vbWidth / 2
      const centerY = vbHeight / 2
      const oldZoom = zoom.value
      zoom.value = Math.max(0.2, zoom.value / 1.2)
      panX.value = centerX - (centerX - panX.value) * (zoom.value / oldZoom)
      panY.value = centerY - (centerY - panY.value) * (zoom.value / oldZoom)
      clampPan()
    }

    const resetView = () => {
      zoom.value = 1
      panX.value = 0
      panY.value = 0
      ElMessage.info(t('smartRouting.mapCenter.pathPlanning.routeCreate.resetView'))
    }

    // ===== 应用选中的地图参数 =====
    const applyMapConfig = (mapItem) => {
      if (!mapItem) return
      currentMapUrl.value = mapItem.mapImageUrl || mapItem.url || currentMapUrl.value

      if (mapItem.resolution !== undefined && mapItem.resolution !== null) {
        mapConfig.res = parseFloat(mapItem.resolution)
      }
      if (mapItem.originX !== undefined && mapItem.originX !== null) {
        mapConfig.ox = parseFloat(mapItem.originX)
      }
      if (mapItem.originY !== undefined && mapItem.originY !== null) {
        mapConfig.oy = parseFloat(mapItem.originY)
      }
      if (mapItem.originYaw !== undefined && mapItem.originYaw !== null) {
        mapConfig.yaw0 = parseFloat(mapItem.originYaw)
      } else if (mapItem.yaw0 !== undefined && mapItem.yaw0 !== null) {
        mapConfig.yaw0 = parseFloat(mapItem.yaw0)
      }

      loadMapSize(currentMapUrl.value)
    }

    // ===== 下拉切换地图 =====
    const handleMapChange = (mapCode) => {
      const mapItem = mapList.value.find(m => m.code === mapCode)
      if (!mapItem) return

      if (!props.isEdit) {
        if (waypoints.value.length > 0) {
          waypoints.value = []
          expandedKeys.value = []
          editingId.value = null
          ElMessage.info(t('smartRouting.mapCenter.pathPlanning.routeCreate.mapChangedClear'))
        }
        applyMapConfig(mapItem)
        return
      }

      const hasWorldCoords = waypoints.value.length > 0 &&
        waypoints.value.every(w => w.worldX !== undefined && w.worldX !== null)

      if (waypoints.value.length > 0) {
        if (hasWorldCoords) {
          needRecalcPixel.value = true
          ElMessage.info(t('smartRouting.mapCenter.pathPlanning.routeCreate.mapChangedRecalc'))
        } else {
          waypoints.value = []
          expandedKeys.value = []
          editingId.value = null
          ElMessage.info(t('smartRouting.mapCenter.pathPlanning.routeCreate.mapChangedClear'))
        }
      }

      applyMapConfig(mapItem)
    }

    // ===== 地图尺寸加载 =====
    const loadMapSize = (url) => {
      if (!url) return
      const img = new Image()
      img.src = url
      img.onload = () => {
        mapNaturalWidth.value = img.naturalWidth
        mapNaturalHeight.value = img.naturalHeight

        if (needRecalcPixel.value) {
          needRecalcPixel.value = false
          recalcWaypointsByWorld()
        } else if (!editDataInitialized.value) {
          editDataInitialized.value = true
          loadEditData()
        }
      }
      img.onerror = () => {
        ElMessage.warning(t('smartRouting.mapCenter.pathPlanning.routeCreate.mapLoadFailed'))
        mapNaturalWidth.value = 0
        mapNaturalHeight.value = 0
        if (!editDataInitialized.value) {
          editDataInitialized.value = true
          loadEditData()
        }
      }
    }

    const recalcWaypointsByWorld = () => {
      if (!mapReady.value) return
      waypoints.value = waypoints.value.map(w => {
        if (w.worldX === undefined || w.worldX === null) return w
        const { pixelX, pixelY } = worldToPixel(w.worldX, w.worldY)
        const px = Math.round(pixelX)
        const py = Math.round(pixelY)
        return {
          ...w,
          x: px,
          y: py,
          worldX: w.worldX,
          worldY: w.worldY
        }
      })
    }

    // ✅ 加载编辑数据：x/y 优先，其次 pixelX/pixelY，最后 worldX/worldY 反算
    const loadEditData = () => {
      if (!props.isEdit || !props.editData) return
      const data = props.editData
      formData.name = data.name || ''
      formData.mapCode = data.mapCode || ''
      formData.pathType = data.pathType || ''
      formData.voiceCode = data.voiceCode || ''
      formData.deviceChannel = data.deviceChannel || ''
      formData.desc = data.desc || ''

      if (data.waypoints && data.waypoints.length) {
        waypoints.value = data.waypoints.map((wp, idx) => {
          let px = wp.x ?? wp.pixelX
          let py = wp.y ?? wp.pixelY

          // 都没有就用 worldX/worldY 反算
          if ((px == null || py == null) && wp.worldX != null && wp.worldY != null && mapReady.value) {
            const p = worldToPixel(wp.worldX, wp.worldY)
            px = p.pixelX
            py = p.pixelY
          }

          const x = Math.round(Number(px) || 0)
          const y = Math.round(Number(py) || 0)

          return {
            ...wp,
            id: wp.id || idx + 1,
            x,
            y,
            worldX: wp.worldX != null ? Number(wp.worldX) : undefined,
            worldY: wp.worldY != null ? Number(wp.worldY) : undefined,
            yaw: Number(wp.yaw) || 0,
            actionCode: wp.actionCode || '',
            speechText: wp.speechText || '',
            resource: wp.resource || ''
          }
        })
        nextId = Math.max(...waypoints.value.map(w => w.id), 0) + 1
      }
      expandedKeys.value = []
      editingId.value = null
    }

    // ===== 表格展开/收起 =====
    const handleExpandChange = (row, expandedRows) => {
      if (expandedRows.length > 0) {
        const lastExpanded = expandedRows[expandedRows.length - 1]
        expandedKeys.value = [lastExpanded.id]
        editingId.value = lastExpanded.id
        waypoints.value.forEach(w => {
          if (w.id !== lastExpanded.id) {
            waypointsTableRef.value?.toggleRowExpansion(w, false)
          }
        })
      } else {
        expandedKeys.value = []
        editingId.value = null
      }
    }

    const handleRowClick = (row, column, event) => {
      if (event && event.target && event.target.closest('.el-button')) return
      if (event && event.target && event.target.closest('.waypoint-edit')) return
      waypointsTableRef.value?.toggleRowExpansion(row)
    }

    const selectWaypoint = (index) => {
      const row = waypoints.value[index]
      if (!row) return
      if (expandedKeys.value.includes(row.id)) {
        waypointsTableRef.value?.toggleRowExpansion(row, false)
      } else {
        waypointsTableRef.value?.toggleRowExpansion(row, true)
      }
    }

    // ✅ 添加航点：左下角像素 (0, H-1)，x/y 直接用整数
    const addWaypoint = () => {
      if (!mapReady.value) {
        ElMessage.warning(t('smartRouting.mapCenter.pathPlanning.routeCreate.mapNotReady'))
        return
      }
      const vbHeight = mapNaturalHeight.value
      const { pixelX, pixelY } = viewBoxToPixel(0, vbHeight - 1)
      const { worldX, worldY } = pixelToWorld(pixelX, pixelY)

      const newPoint = {
        id: nextId++,
        name: `途经点${waypoints.value.length}`,
        x: pixelX,
        y: pixelY,
        worldX,
        worldY,
        yaw: 0, actionCode: '', speechText: '', resource: ''
      }
      waypoints.value.push(newPoint)
      expandedKeys.value = [newPoint.id]
      editingId.value = newPoint.id
      assertRoundTrip(pixelX, pixelY)
    }

    const removeWaypoint = (index) => {
      const row = waypoints.value[index]
      if (!row) return
      const rowId = row.id
      waypoints.value.splice(index, 1)
      if (expandedKeys.value.includes(rowId)) {
        expandedKeys.value = []
        editingId.value = null
      }
    }

    const initSortable = () => {
      const table = document.querySelector('.waypoints-table .el-table__body-wrapper tbody')
      if (!table) return
      Sortable.create(table, {
        handle: '.drag-handle',
        animation: 150,
        onEnd: (evt) => {
          const { oldIndex, newIndex } = evt
          if (oldIndex === newIndex) return
          const item = waypoints.value.splice(oldIndex, 1)[0]
          waypoints.value.splice(newIndex, 0, item)
        }
      })
    }

    // ✅ 保存：下发 x/y 整数，不带 pixelX/pixelY
    const handleSave = async () => {
      try {
        await formRef.value?.validate()
      } catch { return }

      if (waypoints.value.length < 2) {
        ElMessage.warning(t('smartRouting.mapCenter.pathPlanning.routeCreate.waypointsMin'))
        return
      }

      saving.value = true
      try {
        const cleanWaypoints = waypoints.value.map((w, idx) => ({
          id: w.id,
          name: w.name || `途经点${idx + 1}`,
          x: Math.round(Number(w.x ?? w.pixelX) || 0),
          y: Math.round(Number(w.y ?? w.pixelY) || 0),
          worldX: Number((Number(w.worldX) || 0).toFixed(3)),
          worldY: Number((Number(w.worldY) || 0).toFixed(3)),
          yaw: Number(w.yaw) || 0,
          actionCode: w.actionCode || null,
          speechText: w.speechText || '',
          resource: w.resource || ''
        }))

        const params = {
          name: formData.name,
          mapCode: formData.mapCode,
          pathType: formData.pathType,
          voiceCode: formData.voiceCode,
          deviceChannel: formData.deviceChannel,
          desc: formData.desc,
          waypoints: cleanWaypoints
        }

        let res
        if (props.isEdit) {
          params.code = props.routeCode
          res = await reqPathUpdate(params)
        } else {
          res = await reqPathCreate(params)
        }
        ElMessage.success(props.isEdit
          ? t('smartRouting.mapCenter.pathPlanning.routeCreate.updateSuccess')
          : t('smartRouting.mapCenter.pathPlanning.routeCreate.createSuccess'))
        emit('success', res.data || res)
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error(t('smartRouting.mapCenter.pathPlanning.routeCreate.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    const handleCancel = () => {
      emit('cancel')
    }

    // ===== 加载地图列表 =====
    const getMapPage = async () => {
      const query = { keyword: '', limit: 100 }
      const res = await reqMapList(query)
      mapList.value = res || []

      let initialMap = null
      if (props.isEdit && props.editData && props.editData.mapCode) {
        initialMap = mapList.value.find(m => m.code === props.editData.mapCode)
      }
      if (!initialMap && mapList.value.length > 0) {
        initialMap = mapList.value[0]
      }

      if (initialMap) {
        if (!formData.mapCode) {
          formData.mapCode = initialMap.code
        }
        applyMapConfig(initialMap)
      } else {
        loadMapSize(currentMapUrl.value)
      }
    }

    const getVoiceList = async () => {
      const res = await reqVoiceList({ keyword: '', limit: 100 })
      voiceList.value = res
    }

    const getActionList = async () => {
      const res = await reqActionList({ keyword: '', limit: 100 })
      actionList.value = res
    }

    getMapPage()
    getVoiceList()
    getActionList()

    onMounted(() => {
      setTimeout(initSortable, 500)
    })

    watch(() => props.isFullscreen, () => {
      isFull.value = props.isFullscreen
    }, { immediate: true })

    return {
      formRef, formData, formRules, waypoints, pathTypeOptions,
      expandedKeys, hoverCoords, saving, mapContainer,
      mousePosition, editingId, zoom, panX, panY, isDragging,
      mapList, voiceList, actionList, mapConfig,
      currentMapUrl, mapNaturalWidth, mapNaturalHeight, waypointsTableRef, isFull,
      mapReady, metersToPixels, fmtWorld,
      handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave,
      handleWheel, zoomIn, zoomOut, resetView, handleExpandChange,
      handleRowClick, selectWaypoint, addWaypoint, removeWaypoint,
      handleSave, handleCancel, handleMapChange
    }
  }
}
</script>

<style scoped>
/* ===== 整体：上下两层 ===== */
.route-create-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #f5f7fa;
}

/* 上层：左右两栏 */
.route-create-body {
  flex: 1;
  display: flex;
  align-items: stretch; 
  min-height: 0;
  overflow: hidden;
}

/* ===== 左侧：地图区域 ===== */
.map-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  /* margin: 12px 6px 0 12px; */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  min-width: 400px;
}
.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
  flex-wrap: wrap;
  gap: 8px;
}
.map-title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.map-title .el-icon-location-outline {
  color: #409eff;
  margin-right: 8px;
}
.route-id {
  font-size: 13px;
  font-weight: 400;
  color: #86909c;
  margin-left: 4px;
}
.map-hint {
  padding: 8px 16px;
  font-size: 12px;
  color: #86909c;
  background: #f8fafc;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 6px;
}
.map-hint .el-icon-info {
  color: #409eff;
  font-size: 14px;
}
.map-container {
  flex: 1;
  position: relative;
  min-height: 400px;
  background: #fafbfc;
  overflow: hidden;
  cursor: grab;
  user-select: none;
}
.map-container:active {
  cursor: grabbing;
}
.map-svg {
  display: block;
  width: 100%;
  height: 100%;
  background-color: #0b0d10;
}
.map-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #86909c;
  font-size: 13px;
  background: #fafbfc;
}
.map-zoom-info {
  position: absolute;
  bottom: 12px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}
.map-coords {
  position: absolute;
  bottom: 12px;
  right: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

/* ===== 右侧：表单面板 ===== */
.form-panel {
  width: 520px;
  background: #ffffff;
  border-radius: 8px;
  /* margin: 12px 12px 0 6px; */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
}
.form-scroll {
  flex: 1;
  padding: 0px 0px 0px 24px;
  overflow-y: auto;
}
.form-scroll :deep(.el-form-item) {
  margin-bottom: 14px;
}
.form-scroll :deep(.el-form-item__label) {
  font-size: 13px;
  color: #606266;
}
.create-form {
  width: 100%;
}

.waypoints-section {
  margin-top: 4px;
}
.waypoints-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.waypoints-title {
  font-size: 13px;
  font-weight: 500;
  color: #1d2129;
}

.ellipsis-text {
  display: inline-block;
  max-width: 150px;       /* 根据实际情况调整 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.waypoints-table {
  width: 100%;
}
.waypoints-table :deep(.el-table__header th) {
  background: #f7f9fb;
  font-size: 12px;
  color: #606266;
  font-weight: 500;
}
.waypoints-table :deep(.el-table__body td) {
  font-size: 12px;
  padding: 4px 0;
}
.waypoints-table :deep(.el-table__row) {
  cursor: pointer;
}
.waypoints-table :deep(.el-table__body-wrapper) {
  overflow-y: auto;
}
.waypoints-table :deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 6px;
}
.waypoints-table :deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

.waypoint-index {
  color: #409eff;
  font-weight: 500;
  margin-right: 4px;
}
.waypoint-name {
  color: #1d2129;
}
.drag-handle {
  cursor: grab;
  color: #c0c4cc;
}
.drag-handle:hover {
  color: #409eff;
}

.waypoints-table :deep(.el-table__expanded-cell) {
  padding: 0 !important;
  background: #f8fafc;
}
.waypoint-edit {
  padding: 14px 16px;
  background: #f8fafc;
  border-top: 1px solid #e9edf2;
}
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9edf2;
}
.edit-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
}
.waypoint-edit :deep(.el-form-item) {
  margin-bottom: 10px;
}
.waypoint-edit :deep(.el-form-item__label) {
  font-size: 12px;
  color: #606266;
}

/* ===== 底部操作栏 ===== */
.route-create-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0px 0px;
  background: #ffffff;
  border-top: 1px solid #ebeef5;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  margin-top: 24px;
}
.footer-info {
  font-size: 12px;
  color: #86909c;
  font-family: 'Courier New', monospace;
  line-height: 1.6;
}
.footer-actions {
  display: flex;
  gap: 10px;
}

:deep(.el-table__expand-icon) {
  display: none;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .form-panel { width: 400px; }
}
@media (max-width: 768px) {
  .route-create-body { flex-direction: column; }
  .map-panel { margin: 8px; min-height: 300px; }
  .form-panel { width: auto; margin: 0 8px; }
  .route-create-footer { flex-direction: column; gap: 8px; align-items: flex-start; }
}
</style>